import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { companyInfo } from "src/data/general";
import { GuideSection } from "./GuideSection";

const ComplaintsPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Complaints Procedure | Letting Engine</title>
        <meta
          name="description"
          content="Letting Engine complaints procedure. How to raise a concern and access independent redress through the Property Redress Scheme (PRS)."
        />
      </Helmet>

      <section className="pt-40 pb-16 w-full bg-bg-porcelain">
        <div className="max-w-[900px] mx-auto px-[5%]">
          <h1 className="font-teko text-6xl text-text-obsidian mb-4">
            Complaints Procedure
          </h1>
          <p className="font-manrope text-sm text-text-slate mb-2">
            Last updated: June 2026
          </p>
          <p className="font-manrope text-base text-text-slate leading-relaxed">
            Letting Engine is committed to providing an exceptional service. If
            something has gone wrong, we want to hear from you and will work to
            resolve your concern promptly and fairly.
          </p>
        </div>
      </section>

      <GuideSection eyebrow="Step 1" title="Contact Us First">
        <div className="max-w-3xl space-y-4 font-manrope text-base text-text-slate leading-relaxed">
          <p>
            Please raise your complaint with us in the first instance so we can
            investigate and respond. You can contact us by email or telephone:
          </p>
          <ul className="space-y-2 pl-4">
            <li>
              Email:{" "}
              <a
                href={`mailto:${companyInfo.email}`}
                className="text-brand-gold underline"
              >
                {companyInfo.email}
              </a>
            </li>
            <li>
              Telephone:{" "}
              <a
                href={`tel:${companyInfo.phone.replace(/[^\d+]/g, "")}`}
                className="text-brand-gold underline"
              >
                {companyInfo.phone}
              </a>
            </li>
            <li>Post: {companyInfo.address.replace(/;/g, ", ")}</li>
          </ul>
          <p>
            Please include your name, contact details, property address (if
            applicable), and a clear description of your complaint together with
            any supporting documents. We aim to acknowledge your complaint within
            three working days.
          </p>
        </div>
      </GuideSection>

      <GuideSection
        eyebrow="Step 2"
        title="Our Investigation"
        className="bg-bg-porcelain"
      >
        <div className="max-w-3xl space-y-4 font-manrope text-base text-text-slate leading-relaxed">
          <p>
            A member of our management team will review your complaint, which
            may include speaking with the staff involved and examining relevant
            records. We will keep you informed of progress and aim to provide a
            full written response within 15 working days of acknowledgement.
          </p>
          <p>
            If we need more time — for example, where a third party must be
            consulted — we will explain the delay and give you a revised
            timeframe.
          </p>
        </div>
      </GuideSection>

      <GuideSection eyebrow="Step 3" title="Independent Redress (PRS)">
        <div className="max-w-3xl space-y-4 font-manrope text-base text-text-slate leading-relaxed">
          <p>
            Letting Engine is a member of the{" "}
            <a
              href="https://www.theprs.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-gold underline"
            >
              Property Redress Scheme (PRS)
            </a>
            , an independent redress scheme approved by the Secretary of State.
            If you are not satisfied with our final response, or if eight weeks
            have passed since you first raised your complaint without resolution,
            you may refer your complaint to PRS for independent adjudication.
          </p>
          <p>
            PRS contact details:
          </p>
          <ul className="space-y-2 pl-4">
            <li>
              Website:{" "}
              <a
                href="https://www.theprs.co.uk/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-gold underline"
              >
                www.theprs.co.uk
              </a>
            </li>
            <li>Email: info@theprs.co.uk</li>
            <li>Telephone: 0333 016 4020</li>
          </ul>
          <p>
            PRS will confirm whether your complaint falls within their remit and
            guide you through their process. Their decision is binding on Letting
            Engine where applicable under the scheme rules.
          </p>
        </div>
      </GuideSection>

      <GuideSection
        eyebrow="Related"
        title="Further Information"
        className="bg-bg-porcelain"
      >
        <div className="max-w-3xl font-manrope text-base text-text-slate leading-relaxed">
          <p className="mb-6">
            For regulatory and membership information, see our{" "}
            <Link to="/about-us#regulatory-stack" className="text-brand-gold underline">
              qualifications and compliance page
            </Link>
            . For privacy-related concerns, see our{" "}
            <Link to="/policies/privacy-policy" className="text-brand-gold underline">
              Privacy Policy
            </Link>
            .
          </p>
          <Link
            to="/contact-us"
            className="inline-block px-8 py-3 bg-text-obsidian text-white font-teko text-xl uppercase tracking-wide hover:bg-brand-yellow hover:text-black transition-all"
          >
            Contact Us
          </Link>
        </div>
      </GuideSection>
    </>
  );
};

export default ComplaintsPage;
